import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  CASHIER_ROLE_LABEL,
  CASHIER_ROLE_LABEL_KEY,
  REGISTRATION_STATUSES,
  REGISTRATION_STATUS_LABEL,
  REGISTRATION_STATUS_LABEL_KEY,
  useCashierFiscalProfile,
  useFiscalFieldsEnabled,
  useFiscalReferenceCodes,
  useLegalEntities,
  useLocationTaxObject,
  useSaveCashierFiscalProfile,
  useSaveLocationTaxObject,
  useSaveTerminalPrro,
  useTerminalPrro,
  type CashierFiscalProfile,
  type CashierFiscalProfileBody,
  type CashierFiscalRole,
  type LegalEntity,
  type LocationTaxObject,
  type LocationTaxObjectBody,
  type ReferenceCode,
  type RegistrationStatus,
  type TerminalPrro,
  type TerminalPrroBody,
} from '@/hooks/use-fiscal-registration';
import { useT } from '@/i18n/use-t';
import {
  AddressFields,
  Banner,
  Field,
  SelectInput,
  TextInput,
  addressBody,
  addressDraft,
  serverError,
  type AddressDraft,
} from './fiscal-form-fields';
import {
  textOrNull,
  validateCashierProfile,
  validatePrro,
  validateTaxObject,
  type FieldErrors,
} from './fiscal-validation';
import { KatottgPicker, filterObjectTypes } from './fiscal-reference-pickers';

/**
 * PFC-10 — the fiscal fields of a location, a terminal and a cashier, as
 * self-contained panels the owning pages embed with one line each.
 *
 * A panel renders nothing — and asks the API nothing — unless the tenant is
 * entitled to the fiscal cabinet and the user holds `fiscal.cabinet.write`
 * (`useFiscalFieldsEnabled`, which also needs the Ukraine pack). So the
 * location, terminal and cashier pages are unchanged for every other tenant
 * and user, and those modules' own APIs never carry fiscal data: each panel
 * reads and writes through the fiscal cabinet's routes.
 */

function PanelShell({ children }: { children: ReactNode }) {
  const t = useT('admin_ui');
  return (
    <section
      aria-label={t('fiscalCabinet.panel.title', 'Fiscal registration (Ukraine)')}
      className="space-y-4 rounded-md border border-border bg-card p-5"
    >
      <div>
        <h2 className="text-sm font-medium">
          {t('fiscalCabinet.panel.title', 'Fiscal registration (Ukraine)')}
        </h2>
        <p className="text-xs text-muted-foreground">
          {t(
            'fiscalCabinet.panel.help',
            'Our registration data for the tax service. Saving here does not send anything to the tax service.',
          )}
        </p>
      </div>
      {children}
    </section>
  );
}

function Loading() {
  const t = useT('admin_ui');
  return <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>;
}

function LoadFailed() {
  const t = useT('admin_ui');
  return (
    <p className="text-sm text-destructive">
      {t('fiscalCabinet.loadError', 'Failed to load the fiscal cabinet.')}
    </p>
  );
}

/** Active taxpayers, plus the linked one even if since retired, so it stays shown. */
function selectableEntities(all: LegalEntity[], currentId: string): LegalEntity[] {
  return all.filter((entity) => entity.active || entity.id === currentId);
}

function NoLegalEntity() {
  const t = useT('admin_ui');
  return (
    <p className="text-sm text-muted-foreground">
      {t(
        'fiscalCabinet.panel.noLegalEntity',
        'Add the legal entity this belongs to first, under Fiscal cabinet → Legal entities.',
      )}{' '}
      <Link className="underline" to="/fiscal-cabinet/legal-entities">
        {t('fiscalCabinet.tab.legalEntities', 'Legal entities')}
      </Link>
    </p>
  );
}

function LegalEntitySelect({
  id,
  value,
  onChange,
  entities,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  entities: LegalEntity[];
}) {
  const t = useT('admin_ui');
  return (
    <SelectInput id={id} value={value} onChange={onChange}>
      <option value="">{t('fiscalCabinet.panel.chooseLegalEntity', 'Choose…')}</option>
      {entities.map((entity) => (
        <option key={entity.id} value={entity.id}>
          {entity.name} · {entity.tin}
        </option>
      ))}
    </SelectInput>
  );
}

/** Code options, keeping a stored code the list does not know (a newer edition may drop it). */
export function CodeOptions<C extends number | string>({
  codes,
  current,
}: {
  codes: ReferenceCode<C>[];
  current: C | null;
}) {
  const known = current === null || codes.some((entry) => entry.code === current);
  return (
    <>
      {!known && <option value={String(current)}>{String(current)}</option>}
      {codes.map((entry) => (
        <option key={String(entry.code)} value={String(entry.code)}>
          {entry.code} — {entry.name}
        </option>
      ))}
    </>
  );
}

function StatusAndDate({
  idPrefix,
  status,
  date,
  onStatus,
  onDate,
  errors,
  statusName,
  dateName,
}: {
  idPrefix: string;
  status: RegistrationStatus;
  date: string;
  onStatus: (value: RegistrationStatus) => void;
  onDate: (value: string) => void;
  errors: FieldErrors;
  statusName: string;
  dateName: string;
}) {
  const t = useT('admin_ui');
  return (
    <>
      <Field
        id={`${idPrefix}-status`}
        label={t('fiscalCabinet.registration.field.status', 'Registration status')}
        errors={errors}
        name={statusName}
      >
        <SelectInput
          id={`${idPrefix}-status`}
          value={status}
          onChange={(v) => onStatus(v as RegistrationStatus)}
        >
          {REGISTRATION_STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(REGISTRATION_STATUS_LABEL_KEY[s], REGISTRATION_STATUS_LABEL[s])}
            </option>
          ))}
        </SelectInput>
      </Field>
      <Field
        id={`${idPrefix}-date`}
        label={t('fiscalCabinet.registration.field.date', 'Registration date')}
        errors={errors}
        name={dateName}
      >
        <input
          id={`${idPrefix}-date`}
          type="date"
          value={date}
          onChange={(e) => onDate(e.target.value)}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
      </Field>
    </>
  );
}

function useSaveFlow() {
  const t = useT('admin_ui');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  async function run(found: FieldErrors, save: () => Promise<unknown>) {
    setErrors(found);
    setBanner(null);
    setSaved(false);
    if (Object.keys(found).length > 0) return;
    try {
      await save();
      setSaved(true);
    } catch (err) {
      const refused = serverError(err, t);
      setErrors(refused.fields);
      setBanner(refused.banner);
    }
  }
  return { errors, banner, saved, run };
}

function SaveRow({
  pending,
  saved,
  onSave,
}: {
  pending: boolean;
  saved: boolean;
  onSave: () => void;
}) {
  const t = useT('admin_ui');
  return (
    <div className="flex items-center gap-3">
      <Button onClick={onSave} disabled={pending}>
        {pending ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
      </Button>
      {saved && (
        <span className="text-xs text-muted-foreground">
          {t('fiscalCabinet.panel.saved', 'Saved.')}
        </span>
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// Location -> taxation object
// ------------------------------------------------------------------

type TaxObjectDraft = {
  legalEntityId: string;
  katottg: string;
  address: AddressDraft;
  objectTypeCode: string;
  objectName: string;
  stateCode: string;
  rightsCode: string;
  registerAtNonMainPlace: boolean;
  stateRegistrationNumber: string;
  dpsObjectCode: string;
  dpsRegistrationStatus: RegistrationStatus;
  dpsRegisteredAt: string;
};

function taxObjectDraft(row: LocationTaxObject | null, entities: LegalEntity[]): TaxObjectDraft {
  const onlyEntity = entities.filter((e) => e.active);
  return {
    legalEntityId: row?.legalEntityId ?? (onlyEntity.length === 1 ? (onlyEntity[0]?.id ?? '') : ''),
    katottg: row?.katottg ?? '',
    address: addressDraft(row?.address ?? null),
    objectTypeCode: String(row?.objectTypeCode ?? 321),
    objectName: row?.objectName ?? '',
    stateCode: String(row?.stateCode ?? 2),
    rightsCode: String(row?.rightsCode ?? 1),
    registerAtNonMainPlace: row?.registerAtNonMainPlace ?? false,
    stateRegistrationNumber: row?.stateRegistrationNumber ?? '',
    dpsObjectCode: row?.dpsObjectCode ?? '',
    dpsRegistrationStatus: row?.dpsRegistrationStatus ?? 'unregistered',
    dpsRegisteredAt: row?.dpsRegisteredAt ?? '',
  };
}

function taxObjectBody(draft: TaxObjectDraft): LocationTaxObjectBody {
  return {
    legalEntityId: draft.legalEntityId,
    katottg: draft.katottg.trim(),
    address: addressBody(draft.address),
    objectTypeCode: Number(draft.objectTypeCode),
    objectName: textOrNull(draft.objectName),
    stateCode: Number(draft.stateCode),
    rightsCode: Number(draft.rightsCode),
    registerAtNonMainPlace: draft.registerAtNonMainPlace,
    stateRegistrationNumber: textOrNull(draft.stateRegistrationNumber),
    dpsObjectCode: textOrNull(draft.dpsObjectCode),
    dpsRegistrationStatus: draft.dpsRegistrationStatus,
    dpsRegisteredAt: textOrNull(draft.dpsRegisteredAt),
  };
}

function LocationTaxObjectForm({ locationId }: { locationId: string }) {
  const t = useT('admin_ui');
  const view = useLocationTaxObject(locationId, true);
  const entities = useLegalEntities();
  const codes = useFiscalReferenceCodes();
  const save = useSaveLocationTaxObject(locationId);
  const flow = useSaveFlow();
  const [draft, setDraft] = useState<TaxObjectDraft | null>(null);
  const [typeFilter, setTypeFilter] = useState('');

  const row = view.data?.taxObject ?? null;
  // Re-seed the form when the stored record changes (first load, after a save).
  const loaded =
    view.data === undefined || entities.data === undefined ? null : (row?.updatedAt ?? 'none');
  useEffect(() => {
    if (view.data === undefined || entities.data === undefined) return;
    setDraft(taxObjectDraft(view.data.taxObject, entities.data.items));
  }, [loaded]);

  if (view.isLoading || entities.isLoading || codes.isLoading) return <Loading />;
  if (view.isError || entities.isError || codes.isError || codes.data === undefined) {
    return <LoadFailed />;
  }
  if (draft === null) return <Loading />;
  const selectable = selectableEntities(entities.data?.items ?? [], draft.legalEntityId);
  if (selectable.length === 0) return <NoLegalEntity />;

  const set = <K extends keyof TaxObjectDraft>(key: K, value: TaxObjectDraft[K]) =>
    setDraft((current) => (current === null ? current : { ...current, [key]: value }));
  const { errors } = flow;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Field
          id="lto-legal-entity"
          label={t('fiscalCabinet.panel.legalEntity', 'Legal entity')}
          errors={errors}
          name="legalEntityId"
          className="col-span-2"
        >
          <LegalEntitySelect
            id="lto-legal-entity"
            value={draft.legalEntityId}
            onChange={(v) => set('legalEntityId', v)}
            entities={selectable}
          />
        </Field>
        <Field
          id="lto-katottg"
          label={t('fiscalCabinet.taxObject.field.katottg', 'KATOTTG code')}
          errors={errors}
          name="katottg"
          className="col-span-2"
        >
          <TextInput
            id="lto-katottg"
            value={draft.katottg}
            onChange={(v) => set('katottg', v)}
            invalid={errors['katottg'] !== undefined}
            mono
            maxLength={19}
            placeholder="UA80000000000093317"
          />
        </Field>
      </div>

      <KatottgPicker
        code={draft.katottg}
        onPick={(unit) =>
          // The settlement fills its code and all three address parts it
          // implies. A part it does not imply (Kyiv itself has no district) is
          // cleared, so no part of a previously picked place is left behind.
          setDraft((current) =>
            current === null
              ? current
              : {
                  ...current,
                  katottg: unit.code,
                  address: {
                    ...current.address,
                    region: unit.address.region ?? '',
                    district: unit.address.district ?? '',
                    settlement: unit.address.settlement ?? '',
                  },
                },
          )
        }
      />

      <AddressFields
        idPrefix="lto"
        value={draft.address}
        onChange={(next) => set('address', next)}
        errors={errors}
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Field
          id="lto-type"
          label={t('fiscalCabinet.taxObject.field.objectType', 'Object type')}
          errors={errors}
          name="objectTypeCode"
        >
          <Input
            aria-label={t('fiscalCabinet.reference.objectTypeFilter', 'Filter object types')}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            placeholder={t('fiscalCabinet.reference.objectTypeFilter', 'Filter object types')}
            className="mb-1 h-8 text-xs"
          />
          <SelectInput
            id="lto-type"
            value={draft.objectTypeCode}
            onChange={(v) => set('objectTypeCode', v)}
          >
            <CodeOptions
              codes={filterObjectTypes(
                codes.data.objectTypes,
                typeFilter,
                Number(draft.objectTypeCode),
              )}
              current={
                // Offer a stored code the catalogue no longer lists, so it stays selected.
                row !== null && !codes.data.objectTypes.some((c) => c.code === row.objectTypeCode)
                  ? row.objectTypeCode
                  : null
              }
            />
          </SelectInput>
          {(view.data?.referenceWarnings ?? []).some((w) => w.field === 'objectTypeCode') &&
            Number(draft.objectTypeCode) === row?.objectTypeCode && (
              <p role="status" className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                {t(
                  'fiscalCabinet.reference.objectTypeUnknown',
                  'This object type is not in the current DPS catalogue. Check it before filing.',
                )}
              </p>
            )}
        </Field>
        <Field
          id="lto-name"
          label={t('fiscalCabinet.taxObject.field.objectName', 'Object name')}
          errors={errors}
          name="objectName"
        >
          <TextInput
            id="lto-name"
            value={draft.objectName}
            onChange={(v) => set('objectName', v)}
            invalid={errors['objectName'] !== undefined}
          />
        </Field>
        <Field
          id="lto-state"
          label={t('fiscalCabinet.taxObject.field.state', 'Object state')}
          errors={errors}
          name="stateCode"
        >
          <SelectInput id="lto-state" value={draft.stateCode} onChange={(v) => set('stateCode', v)}>
            <CodeOptions codes={codes.data.objectStates} current={row?.stateCode ?? null} />
          </SelectInput>
        </Field>
        <Field
          id="lto-rights"
          label={t('fiscalCabinet.taxObject.field.rights', 'Right to the object')}
          errors={errors}
          name="rightsCode"
        >
          <SelectInput
            id="lto-rights"
            value={draft.rightsCode}
            onChange={(v) => set('rightsCode', v)}
          >
            <CodeOptions codes={codes.data.objectRights} current={row?.rightsCode ?? null} />
          </SelectInput>
        </Field>

        <Field
          id="lto-regnum"
          label={t(
            'fiscalCabinet.taxObject.field.stateRegistrationNumber',
            'State registration number',
          )}
          errors={errors}
          name="stateRegistrationNumber"
        >
          <TextInput
            id="lto-regnum"
            value={draft.stateRegistrationNumber}
            onChange={(v) => set('stateRegistrationNumber', v)}
          />
        </Field>
        <Field
          id="lto-code"
          label={t(
            'fiscalCabinet.taxObject.field.dpsObjectCode',
            'Object code (type + own number)',
          )}
          errors={errors}
          name="dpsObjectCode"
        >
          <TextInput
            id="lto-code"
            value={draft.dpsObjectCode}
            onChange={(v) => set('dpsObjectCode', v)}
            invalid={errors['dpsObjectCode'] !== undefined}
            mono
            maxLength={8}
            placeholder="32100001"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {t(
              'fiscalCabinet.taxObject.objectCodeHelp',
              'Left empty, the next free number is assigned on save.',
            )}
          </p>
        </Field>
        <StatusAndDate
          idPrefix="lto"
          status={draft.dpsRegistrationStatus}
          date={draft.dpsRegisteredAt}
          onStatus={(v) => set('dpsRegistrationStatus', v)}
          onDate={(v) => set('dpsRegisteredAt', v)}
          errors={errors}
          statusName="dpsRegistrationStatus"
          dateName="dpsRegisteredAt"
        />
        <label className="col-span-2 flex items-center gap-2 text-sm md:col-span-4">
          <input
            type="checkbox"
            checked={draft.registerAtNonMainPlace}
            onChange={(e) => set('registerAtNonMainPlace', e.target.checked)}
          />
          {t(
            'fiscalCabinet.taxObject.field.nonMainPlace',
            'Also register at a non-main place of account at this address',
          )}
        </label>
      </div>

      {flow.banner !== null && <Banner>{flow.banner}</Banner>}
      <SaveRow
        pending={save.isPending}
        saved={flow.saved}
        onSave={() => {
          const body = taxObjectBody(draft);
          void flow.run(validateTaxObject(body), () => save.mutateAsync(body));
        }}
      />
    </div>
  );
}

export function LocationTaxObjectPanel({ locationId }: { locationId: string }) {
  const enabled = useFiscalFieldsEnabled();
  if (!enabled) return null;
  return (
    <PanelShell>
      <LocationTaxObjectForm key={locationId} locationId={locationId} />
    </PanelShell>
  );
}

// ------------------------------------------------------------------
// Terminal -> PRRO
// ------------------------------------------------------------------

type PrroDraft = {
  fiscalNumber: string;
  localNumber: string;
  prroName: string;
  prroTypeCode: string;
  offlineModeCode: string;
  registrationStatus: RegistrationStatus;
  registeredAt: string;
};

function prroDraft(row: TerminalPrro | null, displayName: string): PrroDraft {
  return {
    fiscalNumber: row?.fiscalNumber ?? '',
    localNumber: row?.localNumber ?? '',
    prroName: row?.prroName ?? displayName,
    prroTypeCode: row?.prroTypeCode ?? 'M041',
    offlineModeCode: row?.offlineModeCode ?? 'M051',
    registrationStatus: row?.registrationStatus ?? 'unregistered',
    registeredAt: row?.registeredAt ?? '',
  };
}

function prroBody(draft: PrroDraft): TerminalPrroBody {
  return {
    fiscalNumber: textOrNull(draft.fiscalNumber),
    localNumber: draft.localNumber.trim(),
    prroName: draft.prroName.trim(),
    prroTypeCode: draft.prroTypeCode,
    offlineModeCode: draft.offlineModeCode,
    registrationStatus: draft.registrationStatus,
    registeredAt: textOrNull(draft.registeredAt),
  };
}

function TerminalPrroForm({ terminalId }: { terminalId: string }) {
  const t = useT('admin_ui');
  const view = useTerminalPrro(terminalId, true);
  const codes = useFiscalReferenceCodes();
  const save = useSaveTerminalPrro(terminalId);
  const flow = useSaveFlow();
  const [draft, setDraft] = useState<PrroDraft | null>(null);

  const row = view.data?.prro ?? null;
  const loaded = view.data === undefined ? null : (row?.updatedAt ?? 'none');
  useEffect(() => {
    if (view.data === undefined) return;
    setDraft(prroDraft(view.data.prro, view.data.terminal.displayName));
  }, [loaded]);

  if (view.isLoading || codes.isLoading) return <Loading />;
  if (view.isError || codes.isError || codes.data === undefined || view.data === undefined) {
    return <LoadFailed />;
  }
  if (draft === null) return <Loading />;
  const set = <K extends keyof PrroDraft>(key: K, value: PrroDraft[K]) =>
    setDraft((current) => (current === null ? current : { ...current, [key]: value }));
  const { errors } = flow;
  const { location, legalEntity } = view.data;

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        {location === null
          ? t(
              'fiscalCabinet.prro.noLocation',
              'This terminal has no location, so its store and legal entity are unknown.',
            )
          : legalEntity === null
            ? t(
                'fiscalCabinet.prro.noTaxObject',
                'The location {location} has no fiscal registration data yet, so its legal entity is unknown.',
              ).replace('{location}', location.name)
            : t('fiscalCabinet.prro.filesUnder', 'Store {location} · legal entity {entity}')
                .replace('{location}', location.name)
                .replace('{entity}', `${legalEntity.name} (${legalEntity.tin})`)}
        {location !== null && location.dpsObjectCode !== null && (
          <span className="ml-1 font-mono">· {location.dpsObjectCode}</span>
        )}
      </p>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Field
          id="prro-name"
          label={t('fiscalCabinet.prro.field.name', 'PRRO name')}
          errors={errors}
          name="prroName"
          className="col-span-2"
        >
          <TextInput
            id="prro-name"
            value={draft.prroName}
            onChange={(v) => set('prroName', v)}
            invalid={errors['prroName'] !== undefined}
          />
        </Field>
        <Field
          id="prro-local"
          label={t('fiscalCabinet.prro.field.localNumber', 'Local number')}
          errors={errors}
          name="localNumber"
        >
          <TextInput
            id="prro-local"
            value={draft.localNumber}
            onChange={(v) => set('localNumber', v)}
            invalid={errors['localNumber'] !== undefined}
            mono
            maxLength={20}
          />
        </Field>
        <Field
          id="prro-fiscal"
          label={t('fiscalCabinet.prro.field.fiscalNumber', 'Fiscal number')}
          errors={errors}
          name="fiscalNumber"
        >
          <TextInput
            id="prro-fiscal"
            value={draft.fiscalNumber}
            onChange={(v) => set('fiscalNumber', v)}
            invalid={errors['fiscalNumber'] !== undefined}
            mono
            maxLength={10}
          />
        </Field>
        <Field
          id="prro-type"
          label={t('fiscalCabinet.prro.field.type', 'PRRO type')}
          errors={errors}
          name="prroTypeCode"
          className="col-span-2"
        >
          <SelectInput
            id="prro-type"
            value={draft.prroTypeCode}
            onChange={(v) => set('prroTypeCode', v)}
          >
            <CodeOptions codes={codes.data.prroTypes} current={row?.prroTypeCode ?? null} />
          </SelectInput>
        </Field>
        <Field
          id="prro-mode"
          label={t('fiscalCabinet.prro.field.offlineMode', 'Offline mode')}
          errors={errors}
          name="offlineModeCode"
          className="col-span-2"
        >
          <SelectInput
            id="prro-mode"
            value={draft.offlineModeCode}
            onChange={(v) => set('offlineModeCode', v)}
          >
            <CodeOptions
              codes={codes.data.prroOfflineModes}
              current={row?.offlineModeCode ?? null}
            />
          </SelectInput>
        </Field>
        <StatusAndDate
          idPrefix="prro"
          status={draft.registrationStatus}
          date={draft.registeredAt}
          onStatus={(v) => set('registrationStatus', v)}
          onDate={(v) => set('registeredAt', v)}
          errors={errors}
          statusName="registrationStatus"
          dateName="registeredAt"
        />
      </div>

      {flow.banner !== null && <Banner>{flow.banner}</Banner>}
      <SaveRow
        pending={save.isPending}
        saved={flow.saved}
        onSave={() => {
          const body = prroBody(draft);
          void flow.run(validatePrro(body), () => save.mutateAsync(body));
        }}
      />
    </div>
  );
}

export function TerminalPrroPanel({ terminalId }: { terminalId: string }) {
  const enabled = useFiscalFieldsEnabled();
  if (!enabled) return null;
  return (
    <PanelShell>
      <TerminalPrroForm key={terminalId} terminalId={terminalId} />
    </PanelShell>
  );
}

// ------------------------------------------------------------------
// Cashier -> key holder
// ------------------------------------------------------------------

type CashierDraft = {
  legalEntityId: string;
  fullName: string;
  tin: string;
  keySerial: string;
  role: CashierFiscalRole;
  registrationStatus: RegistrationStatus;
  registeredAt: string;
};

function cashierDraft(
  row: CashierFiscalProfile | null,
  displayName: string,
  entities: LegalEntity[],
): CashierDraft {
  const active = entities.filter((e) => e.active);
  return {
    legalEntityId: row?.legalEntityId ?? (active.length === 1 ? (active[0]?.id ?? '') : ''),
    fullName: row?.fullName ?? displayName,
    tin: row?.tin ?? '',
    keySerial: row?.keySerial ?? '',
    role: row?.role ?? 'cashier',
    registrationStatus: row?.registrationStatus ?? 'unregistered',
    registeredAt: row?.registeredAt ?? '',
  };
}

function cashierBody(draft: CashierDraft): CashierFiscalProfileBody {
  return {
    legalEntityId: draft.legalEntityId,
    fullName: draft.fullName.trim(),
    tin: draft.tin.trim(),
    keySerial: textOrNull(draft.keySerial),
    role: draft.role,
    registrationStatus: draft.registrationStatus,
    registeredAt: textOrNull(draft.registeredAt),
  };
}

function CashierFiscalForm({ cashierId }: { cashierId: string }) {
  const t = useT('admin_ui');
  const view = useCashierFiscalProfile(cashierId, true);
  const entities = useLegalEntities();
  const save = useSaveCashierFiscalProfile(cashierId);
  const flow = useSaveFlow();
  const [draft, setDraft] = useState<CashierDraft | null>(null);

  const loaded =
    view.data === undefined || entities.data === undefined
      ? null
      : (view.data.fiscalProfile?.updatedAt ?? 'none');
  useEffect(() => {
    if (view.data === undefined || entities.data === undefined) return;
    setDraft(
      cashierDraft(view.data.fiscalProfile, view.data.cashier.displayName, entities.data.items),
    );
  }, [loaded]);

  if (view.isLoading || entities.isLoading) return <Loading />;
  if (view.isError || entities.isError) return <LoadFailed />;
  if (draft === null) return <Loading />;
  const selectable = selectableEntities(entities.data?.items ?? [], draft.legalEntityId);
  if (selectable.length === 0) return <NoLegalEntity />;
  const set = <K extends keyof CashierDraft>(key: K, value: CashierDraft[K]) =>
    setDraft((current) => (current === null ? current : { ...current, [key]: value }));
  const { errors } = flow;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field
          id="cfp-legal-entity"
          label={t('fiscalCabinet.panel.legalEntity', 'Legal entity')}
          errors={errors}
          name="legalEntityId"
          className="col-span-2"
        >
          <LegalEntitySelect
            id="cfp-legal-entity"
            value={draft.legalEntityId}
            onChange={(v) => set('legalEntityId', v)}
            entities={selectable}
          />
        </Field>
        <Field
          id="cfp-name"
          label={t('fiscalCabinet.cashier.field.fullName', 'Full legal name')}
          errors={errors}
          name="fullName"
          className="col-span-2"
        >
          <TextInput
            id="cfp-name"
            value={draft.fullName}
            onChange={(v) => set('fullName', v)}
            invalid={errors['fullName'] !== undefined}
          />
        </Field>
        <Field
          id="cfp-tin"
          label={t('fiscalCabinet.cashier.field.tin', 'Tax ID (RNOKPP)')}
          errors={errors}
          name="tin"
        >
          <TextInput
            id="cfp-tin"
            value={draft.tin}
            onChange={(v) => set('tin', v)}
            invalid={errors['tin'] !== undefined}
            mono
            maxLength={10}
          />
        </Field>
        <Field
          id="cfp-role"
          label={t('fiscalCabinet.cashier.field.role', 'Role on the PRRO')}
          errors={errors}
          name="role"
        >
          <SelectInput
            id="cfp-role"
            value={draft.role}
            onChange={(v) => set('role', v as CashierFiscalRole)}
          >
            {(['cashier', 'senior_cashier'] as const).map((role) => (
              <option key={role} value={role}>
                {t(CASHIER_ROLE_LABEL_KEY[role], CASHIER_ROLE_LABEL[role])}
              </option>
            ))}
          </SelectInput>
        </Field>
        <Field
          id="cfp-key"
          label={t('fiscalCabinet.cashier.field.keySerial', 'Key certificate serial')}
          errors={errors}
          name="keySerial"
          className="col-span-2"
        >
          <TextInput
            id="cfp-key"
            value={draft.keySerial}
            onChange={(v) => set('keySerial', v)}
            invalid={errors['keySerial'] !== undefined}
            mono
            maxLength={64}
          />
        </Field>
        <StatusAndDate
          idPrefix="cfp"
          status={draft.registrationStatus}
          date={draft.registeredAt}
          onStatus={(v) => set('registrationStatus', v)}
          onDate={(v) => set('registeredAt', v)}
          errors={errors}
          statusName="registrationStatus"
          dateName="registeredAt"
        />
      </div>

      {flow.banner !== null && <Banner>{flow.banner}</Banner>}
      <SaveRow
        pending={save.isPending}
        saved={flow.saved}
        onSave={() => {
          const body = cashierBody(draft);
          void flow.run(validateCashierProfile(body), () => save.mutateAsync(body));
        }}
      />
    </div>
  );
}

export function CashierFiscalPanel({ cashierId }: { cashierId: string }) {
  const enabled = useFiscalFieldsEnabled();
  if (!enabled) return null;
  return (
    <PanelShell>
      <CashierFiscalForm key={cashierId} cashierId={cashierId} />
    </PanelShell>
  );
}
